import React, { useEffect, useRef, useState } from "react";
import AgoraRTC from "agora-rtc-sdk-ng";
import { useNavigate, useLocation } from "react-router-dom";
import "./AgoraCall.css";
import { FaUserAlt } from "react-icons/fa";
const AgoraVideoCall = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { channelName, agoraToken, appId, astrologerId } = location.state || {};
  const clientRef = useRef(null);
  const localAudioTrackRef = useRef(null);
  const localVideoTrackRef = useRef(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [callTime, setCallTime] = useState(0);
  useEffect(() => {
    let interval;
    if (isJoined) {
      interval = setInterval(() => {
        setCallTime((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isJoined]);
  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };
  const initializeClient = async () => {
    try {
      clientRef.current = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
      setupEventListeners();
    } catch (err) {
      setError("Failed to initialize Agora client");
      console.error(err);
    }
  };
  const setupEventListeners = () => {
    const client = clientRef.current;
    if (!client) return;
    client.on("user-published", async (user, mediaType) => {
      try {
        await client.subscribe(user, mediaType);
        if (mediaType === "video" && user.videoTrack) {
          // Video will be played via ref in render, no action needed here
        }
        if (mediaType === "audio" && user.audioTrack) {
          user.audioTrack.play(); // ✅ Play remote audio directly
        }
        updateRemoteUsers();
      } catch (err) {
        setError("Failed to subscribe to user");
        console.error(err);
      }
    });
    client.on("user-unpublished", (user, mediaType) => {
      if (mediaType === "audio" && user.audioTrack) {
        user.audioTrack.stop(); // ✅ Stop audio when unpublished
      }
      updateRemoteUsers();
    });
    client.on("user-left", () => {
      updateRemoteUsers();
    });
  };
  const updateRemoteUsers = () => {
    const users = clientRef.current?.remoteUsers || [];
    setRemoteUsers([...users]);
  };
  const joinChannel = async () => {
    if (!channelName || !agoraToken || !appId) {
      setError("Missing required parameters");
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const client = clientRef.current;
      await client.join(appId, channelName, agoraToken, null);
      localAudioTrackRef.current = await AgoraRTC.createMicrophoneAudioTrack();
      localVideoTrackRef.current = await AgoraRTC.createCameraVideoTrack();
      await client.publish([
        localAudioTrackRef.current,
        localVideoTrackRef.current
      ]);
      setIsJoined(true);
    } catch (err) {
      setError("Failed to join the channel");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  const leaveChannel = async () => {
    setIsLoading(true);
    try {
      localAudioTrackRef.current?.close();
      localVideoTrackRef.current?.close();
      // await clientRef.current?.leave();
      localAudioTrackRef.current = null;
      localVideoTrackRef.current = null;
      await clientRef.current?.leave();
      clientRef.current?.removeAllListeners();
      clientRef.current = null;
      setIsJoined(false);
      setRemoteUsers([]);
      setCallTime(0);
    } catch (err) {
      setError("Failed to leave the channel");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };
  const endCall = async () => {
    await leaveChannel();
    navigate(`/astrologer/call/${astrologerId}`);
  };
  const toggleMic = () => {
    if (localAudioTrackRef.current) {
      const muted = !isAudioMuted;
      localAudioTrackRef.current.setEnabled(!muted);
      setIsAudioMuted(muted);
    }
  };
  const toggleVideo = () => {
    if (localVideoTrackRef.current) {
      const muted = !isVideoMuted;
      localVideoTrackRef.current.setEnabled(!muted);
      setIsVideoMuted(muted);
    }
  };
  useEffect(() => {
    const init = async () => {
      await initializeClient();
      setTimeout(joinChannel, 500);
    };
    init();
    return () => {
      if (isJoined) {
        leaveChannel();
      }
    };
  }, []);
  return (
    <div className="main">
      <h1 className="heading">Video Chat</h1>
      <div className="video-container">
        <div className="video-player">
          <div className="video-label">You</div>
          <div
            ref={(ref) => {
              if (ref && localVideoTrackRef.current) {
                localVideoTrackRef.current.play(ref);
              }
            }}
            style={{ width: "100%", height: "100%" }}
          />
        </div>
        {remoteUsers.map((user) => (
          <div className="video-player" key={user.uid}>
            <div className="video-label">Astrologer</div>
            {user.videoTrack ? (
              <div
                ref={(ref) => {
                  if (ref) {
                    user.videoTrack.play(ref);
                  }
                }}
                style={{ width: "100%", height: "100%" }}
              />
            ) : (
              <div className="fallback-icon">
                <FaUserAlt size={200} color="#888" />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="call-timer-container">
        <div className="call-timer">
          <span className="timer-icon">⏰</span>
          <span className="timer-text">{formatTime(callTime)}</span>
        </div>
      </div>
      <div className="controls">
        <button onClick={endCall} disabled={!isJoined}>
          Leave Call
        </button>
        <button onClick={toggleMic} disabled={!isJoined}>
          {isAudioMuted ? "Unmute Audio" : "Mute Audio"}
        </button>
        <button onClick={toggleVideo} disabled={!isJoined}>
          {isVideoMuted ? "Turn Video On" : "Turn Video Off"}
        </button>
      </div>
    </div>
  );
};
export default AgoraVideoCall;